import {
  createQuestion,
  FormDefinition,
  parseFormDefinition,
  Question,
  REQUIRED_LIKERT_QUESTION_KEYWORDS,
  Section,
  StoredFormDefinition,
} from '@/modules/shared/hr-form-engine';
import type { PmForm } from '@/modules/performance/hr-form-creation/types/form.types';
import { SKILLS_LIKERT_DESCRIPTION, SKILLS_LIKERT_LABELS, SKILLS_LIKERT_QUESTION } from '../constants/review-core.constants';

/**
 * Snapshot stored in PmReviewDetail.PmFormJson (legacy: the serialised PmForm). Unlike a form template it keeps the
 * reviewer's answers, and it is frozen at request time so later edits of the template never change a running review.
 */
export interface PmFormSnapshot {
  Title: string;
  Description?: string;
  AssessmentType?: string;
  ReviewType?: string;
  Category?: string;
  HasSections: boolean;
  FormData: Question[];
  Sections: Section[];
}

export const buildFormSnapshot = (form: PmForm, definition: FormDefinition = parseFormDefinition(form)): PmFormSnapshot => ({
  Title: form.Title,
  Description: form.Description,
  AssessmentType: form.AssessmentType,
  ReviewType: form.ReviewType,
  Category: form.Category || undefined,
  HasSections: definition.HasSections,
  FormData: definition.FormData,
  Sections: definition.Sections,
});

export const parseFormSnapshot = (json?: string): { snapshot: PmFormSnapshot; definition: FormDefinition } | null => {
  if (!json) return null;
  try {
    const raw = JSON.parse(json) as Partial<PmFormSnapshot>;
    const stored: StoredFormDefinition = {
      HasSections: raw.HasSections,
      FormDataJson: JSON.stringify(raw.FormData ?? []),
      SectionsJson: JSON.stringify(raw.Sections ?? []),
    };
    const definition = parseFormDefinition(stored);
    return {
      snapshot: {
        Title: raw.Title ?? '',
        Description: raw.Description,
        AssessmentType: raw.AssessmentType,
        ReviewType: raw.ReviewType,
        Category: raw.Category,
        HasSections: definition.HasSections,
        FormData: definition.FormData,
        Sections: definition.Sections,
      },
      definition,
    };
  } catch {
    return null;
  }
};

export const serializeFormSnapshot = (snapshot: PmFormSnapshot, definition: FormDefinition): string =>
  JSON.stringify({ ...snapshot, HasSections: definition.HasSections, FormData: definition.FormData, Sections: definition.Sections });

export const allQuestions = (definition: { HasSections: boolean; FormData: Question[]; Sections: Section[] }): Question[] =>
  definition.HasSections ? definition.Sections.flatMap((section) => section.Questions) : definition.FormData;

const isSkillsQuestion = (question: Question) =>
  question.FormatType === 'likert-scale' && question.Question.toLowerCase() === SKILLS_LIKERT_QUESTION.toLowerCase();

/** Legacy HasAllRequiredLikertQuestions: only forms with every mandatory topic get the skills question. */
const hasAllRequiredLikertTopics = (definition: FormDefinition): boolean => {
  const texts = allQuestions(definition)
    .filter((question) => question.FormatType === 'likert-scale')
    .map((question) => question.Question.toLowerCase());
  return REQUIRED_LIKERT_QUESTION_KEYWORDS.every((keyword) => texts.some((text) => text.includes(keyword.toLowerCase())));
};

/**
 * Legacy CloneWithSkillsLikert: replaces the peer-chosen skills Likert question of a form (one statement per skill,
 * fixed 4-point labels) so every reviewer scores the same skill set. A form missing a required topic, or a review
 * without skills, is returned unchanged.
 */
export const withSkillsLikert = (definition: FormDefinition, skills: string[]): FormDefinition => {
  // Case-insensitive distinct that keeps the first spelling (legacy Distinct(OrdinalIgnoreCase)).
  const distinctSkills = skills.filter(
    (skill, index) => skill.trim() && skills.findIndex((other) => other.toLowerCase() === skill.toLowerCase()) === index
  );
  if (!distinctSkills.length || !hasAllRequiredLikertTopics(definition)) return definition;

  const skillsQuestion: Question = {
    ...createQuestion('likert-scale'),
    Question: SKILLS_LIKERT_QUESTION,
    QuestionDescription: SKILLS_LIKERT_DESCRIPTION,
    IsRequired: true,
    LikertLabels: [...SKILLS_LIKERT_LABELS],
    LikertStatements: distinctSkills.map((skill) => ({ Statement: skill, Value: [] })),
  };
  const nextPosition = (questions: Question[]) => (questions.length ? Math.max(...questions.map((q) => q.Position)) + 1 : 0);

  if (definition.HasSections && definition.Sections.length) {
    const first = [...definition.Sections].sort((a, b) => a.Position - b.Position)[0];
    return {
      ...definition,
      Sections: definition.Sections.map((section) => {
        const kept = section.Questions.filter((question) => !isSkillsQuestion(question));
        return section.SectionId === first.SectionId
          ? { ...section, Questions: [...kept, { ...skillsQuestion, SectionId: section.SectionId, Position: nextPosition(kept) }] }
          : { ...section, Questions: kept };
      }),
    };
  }

  const kept = definition.FormData.filter((question) => !isSkillsQuestion(question));
  return { ...definition, FormData: [...kept, { ...skillsQuestion, Position: nextPosition(kept) }] };
};

/**
 * Legacy ResolveMatchingPmForm: the most specific published form wins - department-and-designation match first, then
 * department with any designation, then any department with the designation, finally the all-departments / all-designations form.
 */
export const resolveMatchingPmForm = (
  candidates: PmForm[],
  profile: { DepartmentId?: string; DesignationId?: string },
  options: { isIntern?: boolean; category?: string } = {}
): PmForm | undefined => {
  const { isIntern = false, category } = options;
  const departmentId = profile.DepartmentId ?? '';
  const designationId = profile.DesignationId ?? '';

  const pool = candidates
    .filter((form) => Boolean(form.ConfirmationForIntern) === isIntern)
    .filter((form) => !category || form.Category === category);

  const isDesignationConfigured = (form: PmForm) => form.IsAllDesignations || (form.DesignationIds ?? []).length > 0;
  const matchesDepartment = (form: PmForm, allDepartments: boolean) =>
    allDepartments
      ? form.IsAllDepartments
      : !form.IsAllDepartments && (form.DepartmentIds ?? []).includes(departmentId);
  const matchesDesignation = (form: PmForm, allDesignations: boolean) =>
    allDesignations
      ? form.IsAllDesignations || !isDesignationConfigured(form)
      : isDesignationConfigured(form) && !form.IsAllDesignations && (form.DesignationIds ?? []).includes(designationId);

  return (
    pool.find((form) => matchesDepartment(form, false) && matchesDesignation(form, false)) ??
    pool.find((form) => matchesDepartment(form, false) && matchesDesignation(form, true)) ??
    pool.find((form) => matchesDepartment(form, true) && matchesDesignation(form, false)) ??
    pool.find((form) => matchesDepartment(form, true) && matchesDesignation(form, true))
  );
};

/** Legacy FindFlaggedQuestionValue: the answer of the multiple-choice question marked as tier / recommendation. */
export const findFlaggedAnswer = (
  definition: FormDefinition,
  flag: 'IsPromotionTierQuestion' | 'IsPromotionRecommendationQuestion'
): string => allQuestions(definition).find((question) => question.FormatType === 'multiple-choice' && question[flag])?.McOptionsValue ?? '';
